import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const ClientNotes = ({ clientId }) => {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentNote, setCurrentNote] = useState({ name: '', content: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  useEffect(() => {
    if (clientId) {
      fetchNotes();
    }
  }, [clientId]);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/clients/${clientId}/notes`);
      setNotes(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching notes:', error);
      setError('Error al cargar las notas. Por favor, intente nuevamente.');
      setLoading(false);
    }
  };

  const handleOpenDialog = (note = null) => {
    if (note) {
      setCurrentNote(note);
      setIsEditing(true);
    } else {
      setCurrentNote({ name: '', content: '' });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentNote({ name: '', content: '' });
    setIsEditing(false);
  };

  const handleOpenDeleteDialog = (note) => {
    setNoteToDelete(note);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setNoteToDelete(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentNote(prev => ({ ...prev, [name]: value }));
  };

  const handleContentChange = (content) => {
    setCurrentNote(prev => ({ ...prev, content }));
  };

  const handleSaveNote = async () => {
    try {
      if (!currentNote.name.trim()) {
        setError('El nombre de la nota no puede estar vacío');
        return;
      }

      if (isEditing) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/clients/${clientId}/notes/${currentNote.id}`,
          currentNote
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/clients/${clientId}/notes`,
          currentNote
        );
      }
      
      handleCloseDialog();
      fetchNotes();
    } catch (error) {
      console.error('Error saving note:', error);
      setError(`Error al ${isEditing ? 'actualizar' : 'crear'} la nota. Por favor, intente nuevamente.`);
    }
  };

  const handleDeleteNote = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/clients/${clientId}/notes/${noteToDelete.id}`);
      handleCloseDeleteDialog();
      fetchNotes();
    } catch (error) {
      console.error('Error deleting note:', error);
      setError('Error al eliminar la nota. Por favor, intente nuevamente.');
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: es });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link'],
      ['clean']
    ],
  };

  const canModifyNotes = () => {
    return ['Administrador', 'Sistemas'].includes(user.role);
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Notas del cliente</Typography>
        {canModifyNotes() && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            size="small"
          >
            Nueva Nota
          </Button>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : notes.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">No hay notas disponibles</Typography>
          {canModifyNotes() && (
            <Typography variant="body2" color="textSecondary">
              Haga clic en "Nueva Nota" para crear una.
            </Typography>
          )}
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ whiteSpace: 'nowrap' }}>
                <TableCell>Nombre</TableCell>
                <TableCell>Última modificación</TableCell>
                <TableCell>Modificado por</TableCell>
                {canModifyNotes() && <TableCell align="right">Acciones</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {notes.map((note) => (
                <TableRow key={note.id} hover onClick={() => handleOpenDialog(note)} sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <TableCell>{note.name}</TableCell>
                  <TableCell>{formatDate(note.last_modified_at)}</TableCell>
                  <TableCell>{note.modified_by_email}</TableCell>
                  {canModifyNotes() && (
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDialog(note);
                        }}
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDeleteDialog(note);
                        }}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog para crear/editar nota */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{isEditing ? 'Editar Nota' : 'Nueva Nota'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Nombre de la nota"
            type="text"
            fullWidth
            value={currentNote.name}
            onChange={handleInputChange}
            sx={{ mb: 3 }}
          />
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Contenido
          </Typography>
          <Box sx={{ height: 300, mb: 2 }}>
            <ReactQuill
              theme="snow"
              value={currentNote.content || ''}
              onChange={handleContentChange}
              modules={modules}
              style={{ height: 250 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSaveNote} variant="contained" color="primary">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para confirmar eliminación */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de que desea eliminar la nota "{noteToDelete?.name}"? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancelar</Button>
          <Button onClick={handleDeleteNote} color="error">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientNotes;